import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  maxWidth?: number;
  className?: string;
  dimBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  maxWidth = 520,
  className,
  dimBackdrop = true,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 py-6 animate-fade-in",
        dimBackdrop ? "bg-black/50" : "bg-transparent",
      )}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          "relative my-auto w-full max-h-[calc(100vh-3rem)] overflow-y-auto rounded-[20px] bg-white p-6 sm:p-8 shadow-[0_16px_48px_rgba(0,0,0,0.12)]",
          className,
        )}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-[#6c757d] hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
        >
          <X className="h-5 w-5" />
        </button>
        {title ? (
          <div className="mb-5 pr-8">
            <h3 className="text-[22px] font-bold tracking-tight text-[#1a1a1a]">{title}</h3>
            {description ? (
              <p className="mt-1 text-[13px] text-[#6c757d]">{description}</p>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
